import React, { useEffect, useMemo, useState } from "react";
import {
    Backdrop,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Fade,
    LinearProgress,
    Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

import { logInfo } from "../../logging/loggingService";
import { trackTourCompletion } from "../../logging/telemetryService";

const styles = {
    backdrop: {
        color: "#fff",
        zIndex: (theme) => theme.zIndex.drawer + 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
    },
    card: {
        maxWidth: 480,
        width: "100%",
        position: "relative",
    },
    stepIndicator: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 2,
    },
    highlight: {
        backgroundColor: "primary.light",
        color: "primary.contrastText",
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        display: "inline-block",
        fontSize: "0.75rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 1,
    },
};

function GuidedTourStep({ step, onClose }) {
    return (
        <Card sx={styles.card} elevation={8}>
            <CardHeader
                title={step.title}
                action={
                    <Button color="inherit" onClick={onClose} aria-label="Tour abbrechen" startIcon={<CloseRoundedIcon />}>
                        Abbrechen
                    </Button>
                }
            />
            <CardContent>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    {step.description}
                </Typography>
                {step.tip ? (
                    <Box sx={{ backgroundColor: "primary.50", color: "primary.dark", p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Tipp
                        </Typography>
                        <Typography variant="body2">{step.tip}</Typography>
                    </Box>
                ) : null}
            </CardContent>
        </Card>
    );
}

export default function GuidedTour({ open, steps = [], tourId = "default", onClose, onComplete }) {
    const [activeStep, setActiveStep] = useState(0);
    const totalSteps = steps.length;
    const step = steps[activeStep] ?? null;

    useEffect(() => {
        if (open) {
            setActiveStep(0);
            logInfo("Guided tour started", { component: "GuidedTour", tourId, steps: totalSteps });
        }
    }, [open, tourId, totalSteps]);

    const progress = useMemo(() => {
        if (!totalSteps) return 0;
        return ((activeStep + 1) / totalSteps) * 100;
    }, [activeStep, totalSteps]);

    const handleNext = async () => {
        if (activeStep + 1 >= totalSteps) {
            try {
                await trackTourCompletion(tourId, { steps: totalSteps });
            } catch (error) {
                logInfo("Failed to record tour completion", { component: "GuidedTour", error });
            }
            onComplete?.();
            onClose?.();
        } else {
            setActiveStep((prev) => prev + 1);
        }
    };

    const handlePrevious = () => {
        setActiveStep((prev) => Math.max(0, prev - 1));
    };

    if (!open || !step) {
        return null;
    }

    return (
        <Backdrop sx={styles.backdrop} open slotProps={{ root: { 'aria-hidden': false } }}>
            <Fade in timeout={250}>
                <Box sx={{ width: "100%", maxWidth: 540 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Typography sx={styles.highlight}>Schritt {activeStep + 1} von {totalSteps}</Typography>
                        <Box sx={{ flexGrow: 1 }}>
                            <LinearProgress variant="determinate" value={progress} />
                        </Box>
                    </Box>
                    <GuidedTourStep step={step} onClose={onClose} />
                    <CardActions sx={{ mt: 2, justifyContent: "space-between" }}>
                        <Button
                            variant="text"
                            color="inherit"
                            onClick={handlePrevious}
                            startIcon={<ArrowBackRoundedIcon />}
                            disabled={activeStep === 0}
                        >
                            Zurück
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            endIcon={activeStep + 1 >= totalSteps ? <CloseRoundedIcon /> : <ArrowForwardRoundedIcon />}
                        >
                            {activeStep + 1 >= totalSteps ? "Tour beenden" : "Weiter"}
                        </Button>
                    </CardActions>
                </Box>
            </Fade>
        </Backdrop>
    );
}

